/**
 * Weather Map Layers Configuration
 * 
 * This file manages weather model configurations for the weather map component.
 * It can load configuration dynamically from the API or fall back to static defaults.
 * 
 * API Endpoint: /VisualCrossingWebServices/rest/services/models?key=API_KEY
 */

// API endpoint for model configuration
const MODELS_API_PATH = '/VisualCrossingWebServices/rest/services/models';

// Static fallback configuration (used when API is unavailable)
const STATIC_MODELS_CONFIG = {
  version: "1.0",
  models: [
    {
      id: "hybrid",
      layers: [
        { element: "precipcomposite", timeInterval: 300 },
        { element: "preciprate", timeInterval: 300 },
        { element: "preciptype", timeInterval: 300 },
        { element: "hailsize", timeInterval: 300 },
        { element: "temp", timeInterval: 3600 },
        { element: "alerts", timeInterval: 300 },
        { element: "alertswebp", timeInterval: 300 },
        { element: "wind", timeInterval: 300 },
        { element: "windspeed", timeInterval: 300 }
      ]
    },
    {
      id: "radar",
      layers: [
        { element: "precipcomposite", timeInterval: 300 },
        { element: "preciprate", timeInterval: 300 },
        { element: "preciptype", timeInterval: 300 },
        { element: "hailsize", timeInterval: 300 }
      ]
    },
    {
      id: "gfs",
      sourceModel: "gfs_location_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 }
      ]
    },
    {
      id: "hrrr",
      sourceModel: "hrrr_location_2",
      bounds: { minLat: 20, maxLat: 60, minLon: -135, maxLon: -50 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 300 },
        { element: "windspeed", timeInterval: 300 }
      ]
    },
    {
      id: "hrrrsubhourly",
      sourceModel: "hrrrsubhourly_location_2",
      bounds: { minLat: 20, maxLat: 60, minLon: -130, maxLon: -50 },
      layers: [
        { element: "precipcomposite", timeInterval: 900 },
        { element: "preciprate", timeInterval: 900 },
        { element: "preciptype", timeInterval: 900 },
        { element: "temp", timeInterval: 900 },
        { element: "wind", timeInterval: 900 },
        { element: "windspeed", timeInterval: 900 }
      ]
    },
    {
      id: "nam",
      sourceModel: "nam_location_2",
      bounds: { minLat: 20, maxLat: 60, minLon: -135, maxLon: -50 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 300 },
        { element: "windspeed", timeInterval: 300 }
      ]
    },
    {
      id: "iconeu",
      sourceModel: "iconeu_location_2",
      bounds: { minLat: 29.5, maxLat: 70.5, minLon: -23.5, maxLon: 45.0 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 300 },
        { element: "windspeed", timeInterval: 300 }
      ]
    },
    {
      id: "ecmwf",
      sourceModel: "ecmwf_location_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "ecmwfaifs",
      sourceModel: "ecmwfaifs_location_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "aigfs",
      sourceModel: "aigfs_location_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "precipcomposite", timeInterval: 21600 },
        { element: "temp", timeInterval: 21600 },
        { element: "wind", timeInterval: 21600 },
        { element: "windspeed", timeInterval: 21600 }
      ]
    },
    {
      id: "ndfd",
      sourceModel: "ndfd_location_2",
      bounds: { minLat: 20, maxLat: 60, minLon: -130, maxLon: -60 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "icond2",
      sourceModel: "icond2_location_2",
      bounds: { minLat: 43.18, maxLat: 58.08, minLon: -3.94, maxLon: 20.34 },
      layers: [
        { element: "precipcomposite", timeInterval: 900 },
        { element: "preciprate", timeInterval: 900 },
        { element: "preciptype", timeInterval: 900 },
        { element: "temp", timeInterval: 900 },
        { element: "wind", timeInterval: 900 },
        { element: "windspeed", timeInterval: 900 }
      ]
    },
    {
      id: "ukmo",
      sourceModel: "ukmo_global_location_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "ukmo_ukv",
      sourceModel: "ukmo_ukv_location_2",
      bounds: { minLat: 48, maxLat: 63, minLon: -12, maxLon: 5 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 },
        { element: "cloudcover", timeInterval: 3600 },
        { element: "humidity", timeInterval: 3600 }
      ]
    },
    {
      id: "gdps",
      sourceModel: "gdps_location_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "precipcomposite", timeInterval: 10800 },
        { element: "preciprate", timeInterval: 10800 },
        { element: "temp", timeInterval: 10800 },
        { element: "wind", timeInterval: 10800 },
        { element: "windspeed", timeInterval: 10800 }
      ]
    },
    {
      id: "iconglobal",
      sourceModel: "iconglobal_location_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "hrdps",
      sourceModel: "hrdps_location_2",
      bounds: { minLat: 39, maxLat: 70, minLon: -134, maxLon: -50 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "rdps",
      sourceModel: "rdps_location_2",
      bounds: { minLat: 10, maxLat: 75, minLon: -145, maxLon: -50 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "arome",
      sourceModel: "arome_location_2",
      bounds: { minLat: 37.5, maxLat: 55.4, minLon: -12, maxLon: 16 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "arpege",
      sourceModel: "arpege_location_2",
      bounds: { minLat: 20, maxLat: 72, minLon: -32, maxLon: 42 },
      layers: [
        { element: "precipcomposite", timeInterval: 3600 },
        { element: "preciprate", timeInterval: 3600 },
        { element: "preciptype", timeInterval: 3600 },
        { element: "temp", timeInterval: 3600 },
        { element: "wind", timeInterval: 3600 },
        { element: "windspeed", timeInterval: 3600 }
      ]
    },
    {
      id: "camsaq",
      model: "camsaq_2",
      bounds: { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 },
      layers: [
        { element: "pm2p5", timeInterval: 3600 },
        { element: "pm10", timeInterval: 3600 },
        { element: "pm1", timeInterval: 3600 },
        { element: "o3", timeInterval: 10800 },
        { element: "no2", timeInterval: 10800 },
        { element: "so2", timeInterval: 10800 },
        { element: "co", timeInterval: 10800 }
      ]
    },
    {
      id: "ndgdaq",
      model: "ndgdaq_2",
      bounds: { minLat: 20, maxLat: 60, minLon: -135, maxLon: -50 },
      layers: [
        { element: "pm2p5", timeInterval: 3600 },
        { element: "o3", timeInterval: 3600 }
      ]
    }
  ]
};

// Model display names (can be extended by API response)
const DEFAULT_MODEL_NAMES = {
  default: "Default",
  hybrid: "Hybrid",
  radar: "Radar",
  gfs: "GFS",
  hrrr: "HRRR",
  hrrrsubhourly: "HRRR Sub-hourly",
  nam: "NAM",
  iconeu: "ICON-EU",
  ecmwf: "ECMWF",
  ecmwfaifs: "ECMWF AIFS",
  aigfs: "AI GFS",
  ndfd: "NDFD",
  icond2: "ICON-D2",
  ukmo: "UK Met Office",
  ukmo_ukv: "UK Met UKV",
  gdps: "GDPS",
  iconglobal: "ICON Global",
  hrdps: "HRDPS",
  rdps: "RDPS",
  arome: "AROME",
  arpege: "ARPEGE",
  usblended: "US Blended",
  gfes: "GFES Ensemble",
  camsaq: "CAMS Air Quality",
  ndgdaq: "NDGD Air Quality"
};

// Current active configuration (starts with static, can be updated from API)
let activeModelsConfig = STATIC_MODELS_CONFIG;
let modelNames = { ...DEFAULT_MODEL_NAMES };

// Default loop steps configuration
const DEFAULT_LOOP_STEPS = {
  precipcomposite: { stepsBack: 12, stepsForward: 4 },
  preciprate: { stepsBack: 12, stepsForward: 0 },
  preciptype: { stepsBack: 12, stepsForward: 0 },
  hailsize: { stepsBack: 12, stepsForward: 0 },
  temp: { stepsBack: 0, stepsForward: 24 },
  alerts: { stepsBack: 0, stepsForward: 0 },
  alertswebp: { stepsBack: 0, stepsForward: 0 },
  wind: { stepsBack: 12, stepsForward: 12 },
  windspeed: { stepsBack: 12, stepsForward: 12 },
  cloudcover: { stepsBack: 0, stepsForward: 24 },
  humidity: { stepsBack: 0, stepsForward: 24 }
};

// Forecast model loop steps (no past data, forecast only)
const FORECAST_LOOP_STEPS = {
  precipcomposite: { stepsBack: 0, stepsForward: 24 },
  preciprate: { stepsBack: 0, stepsForward: 24 },
  preciptype: { stepsBack: 0, stepsForward: 24 },
  temp: { stepsBack: 0, stepsForward: 24 },
  wind: { stepsBack: 0, stepsForward: 24 },
  windspeed: { stepsBack: 0, stepsForward: 24 },
  cloudcover: { stepsBack: 0, stepsForward: 24 },
  humidity: { stepsBack: 0, stepsForward: 24 }
};

/**
 * Converts timeInterval (seconds) to timeSkip (minutes)
 */
function intervalToTimeSkip(timeInterval) {
  return Math.round(timeInterval / 60);
}

/**
 * Get loop steps for an element based on whether it's a forecast model
 */
function getLoopSteps(element, isForecastModel) {
  if (isForecastModel) {
    return FORECAST_LOOP_STEPS[element] || { stepsBack: 0, stepsForward: 24 };
  }
  return DEFAULT_LOOP_STEPS[element] || { stepsBack: 12, stepsForward: 12 };
}

/**
 * Get the time interval in seconds for a given hours offset from model run time
 * Uses temporalAvailability intervals to determine the correct step size
 * 
 * @param {Object} modelConfig - The model configuration object
 * @param {number} hoursFromModelRun - Hours offset from model run time
 * @returns {number} - Interval in seconds for this time range
 */
function getIntervalForHoursOffset(modelConfig, hoursFromModelRun) {
  const intervals = modelConfig.temporalAvailability?.intervals;
  if (!intervals || intervals.length === 0) {
    // Default to 1 hour if no temporal availability
    return 3600;
  }
  
  // Find the interval that covers this hour offset
  for (const interval of intervals) {
    if (hoursFromModelRun >= interval.startHour && hoursFromModelRun < interval.endHour) {
      return interval.intervalSeconds;
    }
  }
  
  // If beyond all intervals, use the last interval's step
  return intervals[intervals.length - 1].intervalSeconds;
}

/**
 * Get the valid time range for a model
 * 
 * @param {Object} modelConfig - The model configuration object
 * @returns {Object} - { minTime: Date, maxTime: Date, modelRunTime: Date }
 */
function getModelTimeRange(modelConfig) {
  const modelRunTimeEpoch = modelConfig.modelRunTimeEpoch;
  const intervals = modelConfig.temporalAvailability?.intervals;
  
  if (!modelRunTimeEpoch || !intervals || intervals.length === 0) {
    return null;
  }
  
  const modelRunTime = new Date(modelRunTimeEpoch * 1000);
  
  // Find min and max hours from intervals
  let minHours = Infinity;
  let maxHours = -Infinity;
  
  for (const interval of intervals) {
    minHours = Math.min(minHours, interval.startHour);
    maxHours = Math.max(maxHours, interval.endHour);
  }
  
  const minTime = new Date(modelRunTime.getTime() + minHours * 3600 * 1000);
  const maxTime = new Date(modelRunTime.getTime() + maxHours * 3600 * 1000);
  
  return { minTime, maxTime, modelRunTime };
}

/**
 * Snap a time to the nearest valid interval for a model
 * 
 * @param {Date} time - The time to snap
 * @param {Object} modelConfig - The model configuration object
 * @param {string} direction - 'nearest', 'floor', or 'ceil'
 * @returns {Date} - The snapped time
 */
function snapTimeToModelInterval(time, modelConfig, direction = 'nearest') {
  const modelRunTimeEpoch = modelConfig.modelRunTimeEpoch;
  const intervals = modelConfig.temporalAvailability?.intervals;
  
  if (!modelRunTimeEpoch || !intervals || intervals.length === 0) {
    // No temporal availability, just return the time
    return time;
  }
  
  const modelRunTime = new Date(modelRunTimeEpoch * 1000);
  const timeMs = time.getTime();
  const modelRunMs = modelRunTime.getTime();
  
  // Calculate hours from model run
  const hoursFromRun = (timeMs - modelRunMs) / (3600 * 1000);
  
  // Get the interval for this time
  const intervalSeconds = getIntervalForHoursOffset(modelConfig, hoursFromRun);
  const intervalMs = intervalSeconds * 1000;
  
  // Calculate offset from model run in terms of intervals
  const offsetMs = timeMs - modelRunMs;
  
  let snappedOffsetMs;
  if (direction === 'floor') {
    snappedOffsetMs = Math.floor(offsetMs / intervalMs) * intervalMs;
  } else if (direction === 'ceil') {
    snappedOffsetMs = Math.ceil(offsetMs / intervalMs) * intervalMs;
  } else {
    snappedOffsetMs = Math.round(offsetMs / intervalMs) * intervalMs;
  }
  
  return new Date(modelRunMs + snappedOffsetMs);
}

/**
 * Check if a time is valid for a model (within its available range)
 * 
 * @param {Date} time - The time to check
 * @param {Object} modelConfig - The model configuration object
 * @returns {boolean} - True if time is within valid range
 */
function isTimeValidForModel(time, modelConfig) {
  const range = getModelTimeRange(modelConfig);
  if (!range) return true; // No constraints
  
  return time >= range.minTime && time <= range.maxTime;
}

/**
 * Transform raw config into component-ready modelOptions format
 * @param {Object} config - The models configuration (from API or static)
 */
function buildModelOptions(config = activeModelsConfig) {
  const modelOptions = {};
  
  // Models that have historical data (radar-based)
  const hybridModels = ['hybrid', 'radar'];
  
  for (const model of config.models) {
    const isForecastModel = !hybridModels.includes(model.id);
    
    const elements = {};
    let minTimeSkip = Infinity;
    
    // Get time interval - API uses temporalAvailability.intervals[0].intervalSeconds
    // Static config uses layers[].timeInterval
    const defaultIntervalSeconds = model.temporalAvailability?.intervals?.[0]?.intervalSeconds || 3600;
    
    // Handle both API format (layers is string[]) and static format (layers is object[])
    const layers = model.layers || [];
    
    for (const layer of layers) {
      let elementName, timeInterval;
      
      if (typeof layer === 'string') {
        // API format: layer is just the element name string
        elementName = layer;
        timeInterval = defaultIntervalSeconds;
      } else {
        // Static format: layer is an object with element and timeInterval
        elementName = layer.element;
        timeInterval = layer.timeInterval || defaultIntervalSeconds;
      }
      
      const timeSkip = intervalToTimeSkip(timeInterval);
      minTimeSkip = Math.min(minTimeSkip, timeSkip);
      
      elements[elementName] = {
        timeSkip,
        loopSteps: getLoopSteps(elementName, isForecastModel)
      };
    }
    
    const modelConfig = {
      elements,
      timeSkip: minTimeSkip === Infinity ? 60 : minTimeSkip,
      loopSteps: isForecastModel 
        ? { stepsBack: 0, stepsForward: 24 }
        : { stepsBack: 12, stepsForward: 12 }
    };
    
    // Add sourceModel - API uses locationModel, static uses sourceModel
    const sourceModel = model.locationModel || model.sourceModel;
    if (sourceModel) {
      modelConfig.sourceModel = sourceModel;
    }
    
    // Add bounds - default to global if not specified
    modelConfig.bounds = model.bounds || { minLat: -90, maxLat: 90, minLon: -180, maxLon: 180 };
    
    // Add temporal availability from API
    // This defines valid time ranges and intervals for this model
    if (model.temporalAvailability) {
      modelConfig.temporalAvailability = model.temporalAvailability;
    }
    
    // Add model run time (the base time for the forecast)
    if (model.modelRunTimeEpoch) {
      modelConfig.modelRunTimeEpoch = model.modelRunTimeEpoch;
      modelConfig.modelRunTime = model.modelRunTime;
    }
    
    modelOptions[model.id] = modelConfig;
  }
  
  // Add default fallback (same as hybrid)
  if (modelOptions.hybrid) {
    modelOptions.default = { ...modelOptions.hybrid };
    delete modelOptions.default.sourceModel;
  }
  
  return modelOptions;
}

/**
 * Get list of all model IDs for quick-select
 * @param {Object} config - The models configuration (from API or static)
 */
function getQuickSelectModels(config = activeModelsConfig) {
  return config.models.map(m => m.id);
}

/**
 * Generate display name from model ID if not in the names map
 * @param {string} modelId - The model ID
 */
function generateModelName(modelId) {
  // Convert snake_case or camelCase to Title Case
  return modelId
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Build model names map from config, falling back to defaults or auto-generated names
 * @param {Object} config - The models configuration (from API or static)
 */
function buildModelNames(config = activeModelsConfig) {
  const names = { ...DEFAULT_MODEL_NAMES };
  
  for (const model of config.models) {
    if (!names[model.id]) {
      // Use name from API if available, otherwise generate from ID
      names[model.id] = model.name || generateModelName(model.id);
    }
  }
  
  return names;
}

/**
 * Fetch model configuration from API
 * @param {string} baseUrl - The base API URL (e.g., 'https://maps.visualcrossing.com')
 * @param {string} apiKey - The API key
 * @returns {Promise<Object>} - The API response with models configuration
 */
async function fetchModelsConfig(baseUrl, apiKey) {
  const url = `${baseUrl}${MODELS_API_PATH}?key=${apiKey}`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch models config: ${response.status} ${response.statusText}`);
  }
  
  const data = await response.json();
  return data;
}

/**
 * Load models configuration from API and update the active config
 * Falls back to static config on error
 * 
 * @param {string} baseUrl - The base API URL
 * @param {string} apiKey - The API key
 * @returns {Promise<{modelOptions: Object, modelNames: Object, quickSelectModels: Array, fromApi: boolean}>}
 */
async function loadModelsFromApi(baseUrl, apiKey) {
  try {
    if (!apiKey) {
      console.log('No API key provided, using static models config');
      return {
        modelOptions: buildModelOptions(STATIC_MODELS_CONFIG),
        modelNames: buildModelNames(STATIC_MODELS_CONFIG),
        quickSelectModels: getQuickSelectModels(STATIC_MODELS_CONFIG),
        modelsConfig: STATIC_MODELS_CONFIG.models,
        fromApi: false
      };
    }
    
    const apiConfig = await fetchModelsConfig(baseUrl, apiKey);
    
    // Validate the response has the expected structure
    if (!apiConfig.models || !Array.isArray(apiConfig.models)) {
      console.warn('Invalid API response structure, using static config');
      return {
        modelOptions: buildModelOptions(STATIC_MODELS_CONFIG),
        modelNames: buildModelNames(STATIC_MODELS_CONFIG),
        quickSelectModels: getQuickSelectModels(STATIC_MODELS_CONFIG),
        modelsConfig: STATIC_MODELS_CONFIG.models,
        fromApi: false
      };
    }
    
    // Update active configuration
    activeModelsConfig = apiConfig;
    modelNames = buildModelNames(apiConfig);
    
    console.log(`Loaded ${apiConfig.models.length} models from API`);
    
    return {
      modelOptions: buildModelOptions(apiConfig),
      modelNames: modelNames,
      quickSelectModels: getQuickSelectModels(apiConfig),
      modelsConfig: apiConfig.models,  // Include full model configs for accessing model field
      fromApi: true
    };
  } catch (error) {
    console.warn('Failed to load models from API, using static config:', error.message);
    return {
      modelOptions: buildModelOptions(STATIC_MODELS_CONFIG),
      modelNames: buildModelNames(STATIC_MODELS_CONFIG),
      quickSelectModels: getQuickSelectModels(STATIC_MODELS_CONFIG),
      modelsConfig: STATIC_MODELS_CONFIG.models,
      fromApi: false
    };
  }
}

// Build static defaults for initial load (before API call)
const MODEL_OPTIONS = buildModelOptions(STATIC_MODELS_CONFIG);
const QUICK_SELECT_MODELS = getQuickSelectModels(STATIC_MODELS_CONFIG);
const MODEL_NAMES = buildModelNames(STATIC_MODELS_CONFIG);

export { 
  MODEL_OPTIONS, 
  MODEL_NAMES, 
  QUICK_SELECT_MODELS, 
  loadModelsFromApi,
  STATIC_MODELS_CONFIG,
  MODELS_API_PATH,
  // Temporal availability helpers
  getIntervalForHoursOffset,
  getModelTimeRange,
  snapTimeToModelInterval,
  isTimeValidForModel
};
